import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Box, 
  CssBaseline, 
  Drawer, 
  IconButton, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Toolbar, 
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Card,
  CardContent,
  CardActionArea
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Dashboard as DashboardIcon,
  ConfirmationNumber as ConfirmationNumberIcon,
  People as PeopleIcon, 
  LocationOn as LocationOnIcon,
  Security as SecurityIcon,
  ShoppingCart as ShoppingCartIcon, 
  Settings as SettingsIcon,
  QrCodeScanner as QrCodeScannerIcon,
  ReceiptLong as ReceiptLongIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import EntryPass from './EntryPass';
import AccessPoints from './AccessPoints';
import SpotRegistration from './SpotRegistration';
import logo from '../images/pinlogo.jpeg'
import { useAuth } from '../context/AuthContext';
import { useNavigate, Routes, Route, useParams, useLocation, Navigate } from 'react-router-dom';
import DashboardView from './DashboardView';
import api from '../utils/api';

const drawerWidth = 240;

const AdminDashboard = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState('Dashboard');
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Event selection state
  const [events, setEvents] = useState([]);
  const [eventSelectionOpen, setEventSelectionOpen] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Extract event name from URL if present
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    const eventIndex = pathParts.indexOf('event');
    
    if (eventIndex !== -1 && pathParts.length > eventIndex + 1) {
      const eventName = pathParts[eventIndex + 1];
      setSelectedEvent(eventName);
      setEventSelectionOpen(false);
      
      // Set the selected item based on the URL
      if (pathParts.length > eventIndex + 2) {
        const section = pathParts[eventIndex + 2];
        switch (section) {
          case 'dashboard':
            setSelectedItem('Dashboard');
            break;
          case 'entry-passes':
            setSelectedItem('Entry Passes');
            break;
          case 'access-points':
            setSelectedItem('Access Points');
            break;
          case 'spot-registration':
            setSelectedItem('Spot Registration');
            break;
          default:
            setSelectedItem('Dashboard');
        }
      }
    } else {
      fetchEvents();
    }
  }, [location.pathname]);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/api/list-events/');
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleEventSelect = (event) => {
    setSelectedEvent(event.name);
    setEventSelectionOpen(false);
    navigate(`/admin-dashboard/event/${event.name}/dashboard`);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuItemClick = (text) => {
    setSelectedItem(text);
    if (selectedEvent) {
      const route = text.toLowerCase().replace(/\s+/g, '-');
      navigate(`/admin-dashboard/event/${selectedEvent}/${route}`);
    }
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon /> },
    { text: 'Entry Passes', icon: <ConfirmationNumberIcon /> },
    { text: 'Access Points', icon: <SecurityIcon /> },
    { text: 'Spot Registration', icon: <LocationOnIcon /> },
  ];

  const drawer = (
    <div>
      <Toolbar />
      <List>
        <img src="https://sprdh.com/wp-content/uploads/2023/08/sprdh-logo-1.png" style={{ width: '100%', padding: 20, marginTop: -40 }} alt="Logo" />
        {selectedEvent && (
          <Typography variant="subtitle1" sx={{ px: 2, py: 1, fontWeight: 'bold' }}>
            Event: {selectedEvent}
          </Typography>
        )}
        {menuItems.map((item) => (
          <ListItem 
            button 
            key={item.text}
            selected={selectedItem === item.text}
            onClick={() => handleMenuItemClick(item.text)}
            disabled={!selectedEvent}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
        {selectedEvent && (
          <ListItem 
            button 
            onClick={() => {
              setEventSelectionOpen(true);
              navigate('/admin-dashboard');
            }}
          >
            <ListItemText primary="Change Event" />
          </ListItem>
        )}
      </List>
    </div>
  );

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const logoutButton = (
    <IconButton
      color="inherit"
      onClick={handleLogout}
      sx={{ ml: 'auto' }}
      title="Logout"
    >
      <LogoutIcon />
    </IconButton>
  );

  // Event selection screen
  if (eventSelectionOpen) {
    return (
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          sx={{
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` },
          }}
        >
          <Toolbar>
            <Typography variant="h6" noWrap component="div">
              Select an Event
            </Typography>
            {logoutButton}
          </Toolbar>
        </AppBar>
        <Box
          component="nav"
          sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
          aria-label="mailbox folders"
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
        <Box
          component="main"
          sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
        >
          <Toolbar />
          <Typography variant="h4" gutterBottom>
            Select an Event
          </Typography>
          <Grid container spacing={3}>
            {events.map((event) => (
              <Grid item xs={12} sm={6} md={4} key={event.id}>
                <Card>
                  <CardActionArea onClick={() => handleEventSelect(event)}>
                    <CardContent>
                      <Typography variant="h5" component="div">
                        {event.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {event.description || 'No description available'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(event.start_date).toLocaleDateString()} - {new Date(event.end_date).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            Admin Dashboard - {selectedEvent}
          </Typography>
          {logoutButton}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
      >
        <Toolbar />
        {selectedEvent && (
          <Routes>
            <Route path="/" element={<Navigate to={`/admin-dashboard/event/${selectedEvent}/dashboard`} replace />} />
            <Route path="/event/:eventName" element={<DashboardView eventName={selectedEvent} />} />
            <Route path="/event/:eventName/dashboard" element={<DashboardView eventName={selectedEvent} />} />
            <Route path="/event/:eventName/entry-passes" element={<EntryPass eventName={selectedEvent} />} />
            <Route path="/event/:eventName/access-points" element={<AccessPoints eventName={selectedEvent} />} />
            <Route path="/event/:eventName/spot-registration" element={<SpotRegistration eventName={selectedEvent} />} />
          </Routes>
        )}
        
        {!selectedEvent && !eventSelectionOpen && (
          <Typography variant="h6" color="error">
            No event selected. Please select an event first.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default AdminDashboard;